import React from 'react';
import { useTranslation } from 'react-i18next';
import { AnimalFeedHistoryService } from './AnimalFeedHistoryService';
import { AnimalFeedHistoryItem } from '../models/feed-registration';
import { extractApiErrorMessage, notifications } from '../utils';

interface UseAnimalFeedHistoryResult {
  items: AnimalFeedHistoryItem[];
  totalAllocatedGrams: number;
  isBusy: boolean;
  /** True once a fetch has settled, successfully or not - lets callers tell "empty" from "not asked yet". */
  loaded: boolean;
  reload: () => void;
}

/**
 * Loads one animal's feed history. Unlike the per-location hook this takes no farm id: the animal id
 * already identifies the farm, and the endpoint scopes to the caller's own farm.
 */
export const useAnimalFeedHistory = (animalId: string): UseAnimalFeedHistoryResult => {
  const { t } = useTranslation();
  const [items, setItems] = React.useState<AnimalFeedHistoryItem[]>([]);
  const [totalAllocatedGrams, setTotalAllocatedGrams] = React.useState(0);
  const [isBusy, setIsBusy] = React.useState(false);
  const [loaded, setLoaded] = React.useState(false);
  const [reloadToken, setReloadToken] = React.useState(0);

  React.useEffect(() => {
    if (!animalId) {
      setItems([]);
      setTotalAllocatedGrams(0);
      setLoaded(true);
      return;
    }

    const service = new AnimalFeedHistoryService();
    // The animal can change while a request is in flight; ignore all but the newest.
    let cancelled = false;

    setIsBusy(true);

    service
      .getFeedHistory(animalId)
      .then((result) => {
        if (cancelled) return;
        setItems(result.items ?? []);
        setTotalAllocatedGrams(result.totalAllocatedGrams);
      })
      .catch((e: unknown) => {
        if (cancelled) return;
        setItems([]);
        setTotalAllocatedGrams(0);
        notifications.error(extractApiErrorMessage(e, { fallback: t('f-com-api-error') }));
      })
      .finally(() => {
        if (cancelled) return;
        setIsBusy(false);
        setLoaded(true);
      });

    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [animalId, reloadToken]);

  const reload = React.useCallback(() => setReloadToken((token) => token + 1), []);

  return { items, totalAllocatedGrams, isBusy, loaded, reload };
};

export default useAnimalFeedHistory;
